/**
 * Quantity totals that never add pounds to eaches (FR-WORD).
 *
 * A delivery can hold 12 cans and 24.5 lb of brisket. "36.5 units" is neither,
 * so a total is kept per unit and shown as `12 ea · 24.5 lb`.
 */

import { qty } from '@/lib/format';

export type CountUnit = 'ea' | 'lb';

export type UnitTotals = { ea: number; lb: number };

/** Anything that is not `lb` counts as `ea`, the way a PO line does. */
export function countUnit(unit: string | null | undefined): CountUnit {
  return unit === 'lb' ? 'lb' : 'ea';
}

/**
 * Sum `pick(row)` per unit. Pounds are added in thousandths so a long column of
 * weighed lines does not pick up float dust (0.1 + 0.2).
 */
export function totalsByUnit<T>(
  rows: readonly T[],
  pick: (row: T) => { unit: string | null | undefined; quantity: number }
): UnitTotals {
  let ea = 0;
  let lbThousandths = 0;
  for (const row of rows) {
    const { unit, quantity } = pick(row);
    if (!Number.isFinite(quantity)) continue;
    if (countUnit(unit) === 'lb') lbThousandths += Math.round(quantity * 1000);
    else ea += quantity;
  }
  return { ea, lb: lbThousandths / 1000 };
}

/**
 * `12 ea · 24.5 lb`. A unit with nothing in it is left out; no units at all is
 * `none` (or the caller's word).
 */
export function unitTotalsText(totals: UnitTotals, none = 'none'): string {
  const parts: string[] = [];
  if (totals.ea !== 0) parts.push(`${qty(totals.ea)} ea`);
  if (totals.lb !== 0) parts.push(`${qty(totals.lb)} lb`);
  return parts.length === 0 ? none : parts.join(' · ');
}
